// ========================== IMPORTS ==========================
import React, { useState, useEffect } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { Navbar, FooterOverlay } from '../../components';
import { menuCategories } from '../../constants/menuImages';

// ========================== COMPONENT ==========================
const Menu = () => {
  const [activeCategory, setActiveCategory] = useState(null);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActiveCategory(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!activeCategory) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [activeCategory]);

  return (
    <div className={css(styles.page)}>
      <Navbar />

      {/* ================= MENU GRID ================= */}
      <section className={css(styles.container)}>
        <h1 className={css(styles.heading)}>Our Menu</h1>
        <p className={css(styles.subheading)}>
          A curated journey of flavor — explore by category
        </p>

        <div className={css(styles.grid)}>
          {menuCategories.map((category) => (
            <div
              key={category.id}
              className={css(styles.card)}
              onClick={() => setActiveCategory(category)}
            >
              <div className={css(styles.cardOverlay)} />
              <h3 className={css(styles.cardTitle)}>{category.title}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* ================= LIGHTBOX ================= */}
      {activeCategory && (
        <div
          className={css(styles.lightbox)}
          onClick={() => setActiveCategory(null)}
        >
          {/* CLOSE BUTTON */}
          <button
            className={css(styles.closeButton)}
            onClick={(e) => {
              e.stopPropagation();
              setActiveCategory(null);
            }}
            aria-label="Close menu"
          >
            ×
          </button>

          <div
            className={css(styles.lightboxContent)}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={css(styles.lightboxTitle)}>
              {activeCategory.title}
            </h2>

            <div className={css(styles.imagesColumn)}>
              {activeCategory.images.map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`${activeCategory.title} ${index + 1}`}
                  className={css(styles.image)}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <FooterOverlay />
    </div>
  );
};

export default Menu;

// ========================== STYLES ==========================
const styles = StyleSheet.create({
  // ---------- PAGE ----------
  page: {
    backgroundColor: 'var(--color-cream)',
    minHeight: '100vh',
    color: 'var(--color-ink)',
  },

  // ---------- CONTAINER ----------
  container: {
    padding: '11rem 2rem 7rem',
    textAlign: 'center',
  },

  heading: {
    fontFamily: 'var(--font-base)',
    fontSize: 'clamp(3rem,6vw,5.5rem)',
    fontWeight: 500,
    letterSpacing: '-.035em',
    color: 'var(--color-green)',
    marginBottom: '1rem',
  },

  subheading: {
    fontFamily: 'var(--font-alt)',
    fontSize: '1.1rem',
    color: 'var(--color-gray)',
    maxWidth: '600px',
    margin: '0 auto 4rem',
  },

  // ---------- GRID ----------
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },

  card: {
    position: 'relative',
    minHeight: '220px',
    borderRadius: '18px',
    background: 'var(--color-green)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'all 0.4s ease',
    ':hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 18px 45px rgba(23,60,50,.2)',
    },
  },

  cardOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(145deg, rgba(255,255,255,.05), rgba(0,0,0,.12))',
  },

  cardTitle: {
    position: 'relative',
    zIndex: 2,
    fontFamily: 'var(--font-base)',
    fontSize: '1.8rem',
    fontWeight: 500,
    color: 'var(--color-white)',
    letterSpacing: '-.01em',
  },

  // ---------- LIGHTBOX ----------
  lightbox: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.63)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '1rem',
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: '0.4s',
    animationFillMode: 'forwards',
    '@media (max-width:600px)': {
      alignItems: 'flex-start',
      padding: '4.75rem .75rem .75rem',
    },
  },

  lightboxContent: {
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '2rem',
    background: 'var(--color-cream)',
    borderRadius: '20px',
    overscrollBehavior: 'contain',
    animationName: {
      from: { transform: 'scale(0.95)', opacity: 0 },
      to: { transform: 'scale(1)', opacity: 1 },
    },
    animationDuration: '0.4s',
    animationFillMode: 'forwards',
  },

  lightboxTitle: {
    textAlign: 'center',
    fontFamily: 'var(--font-base)',
    color: 'var(--color-green)',
    fontSize: '2rem',
    marginBottom: '2rem',
    scrollBehavior: 'smooth',
  },

  // ---------- IMAGES ----------
  imagesColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },

  image: {
    width: '100%',
    borderRadius: '10px',
    objectFit: 'cover',
    boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
  },

  closeButton: {
    position: 'fixed',
    top: '2rem',
    right: '2rem',
    width: '48px',
    height: '48px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: '50%',
    padding: 0,
    border: 'none',
    color: '#fff',
    fontSize: '32px',
    textAlign: 'center',
    cursor: 'pointer',
    zIndex: 10001,
    '@media (max-width:600px)': {
      top: '1rem',
      right: '1rem',
    },
  },
});
